/**
 * RetailerDropdown custom element
 * Handles open/close logic for the "Hier online kaufen" retailer dropdown on the PDP.
 *
 * Usage: <retailer-dropdown> … </retailer-dropdown>
 */

class RetailerDropdown extends HTMLElement {
  constructor() {
    super();
    this._toggle = null;
    this._panel = null;
    this._handleOutsideClick = this._handleOutsideClick.bind(this);
    this._handleKeydown = this._handleKeydown.bind(this);
  }

  connectedCallback() {
    this._toggle = this.querySelector('.retailer-dropdown__toggle');
    this._panel = this.querySelector('.retailer-dropdown__panel');

    if (!this._toggle || !this._panel) return;

    this._toggle.addEventListener('click', () => this._togglePanel());
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._handleOutsideClick);
    document.removeEventListener('keydown', this._handleKeydown);
  }

  _togglePanel() {
    const isOpen = this._toggle.getAttribute('aria-expanded') === 'true';
    isOpen ? this._close() : this._open();
  }

  _open() {
    this._panel.removeAttribute('hidden');
    this._toggle.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this._handleOutsideClick);
    document.addEventListener('keydown', this._handleKeydown);

    // Move focus to first logo link for keyboard users
    const firstLink = this._panel.querySelector('.retailer-dropdown__link');
    if (firstLink) {
      requestAnimationFrame(() => firstLink.focus({ preventScroll: true }));
    }
  }

  _close() {
    this._toggle.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this._handleOutsideClick);
    document.removeEventListener('keydown', this._handleKeydown);

    // Wait for CSS transition before hiding
    const onTransitionEnd = () => {
      this._panel.setAttribute('hidden', '');
      this._panel.removeEventListener('transitionend', onTransitionEnd);
    };

    this._panel.addEventListener('transitionend', onTransitionEnd);
    // Fallback in case transitionend doesn't fire
    setTimeout(() => {
      if (this._toggle.getAttribute('aria-expanded') === 'false') {
        this._panel.setAttribute('hidden', '');
      }
    }, 300);
  }

  _handleOutsideClick(event) {
    if (!this.contains(event.target)) {
      this._close();
    }
  }

  _handleKeydown(event) {
    if (event.key === 'Escape') {
      this._close();
      this._toggle.focus();
    }
  }
}

customElements.define('retailer-dropdown', RetailerDropdown);
